/* eslint-disable react/no-unknown-property */
import { FC } from 'react';

// PSTUDIO-4120. Two things this component owes the validation badge.
//
// 1. A `testId` passthrough, so a test can distinguish "the pass did not finish"
//    from "the pass found N errors" — the two states share the trigger button
//    and only the glyph tells them apart. (`IconCheck` puts its `test-id` on a
//    wrapping `<span>`; here it sits on the `<svg>` itself, so a locator copied
//    from that one — `span[test-id=...]` — will not match. The e2e uses a
//    tag-less selector.)
//
// 2. `currentColor`, NOT the `#FFA800` this file used to hardcode. That colour
//    is `gold 500`, the DARKEST amber the palette has, and it is the worst
//    reading available here.
//
//    The backdrop these ratios are measured against is NOT white. The trigger
//    sits inside `<NavControls>`, whose `.c-nav-controls` paints an opaque
//    `color(gray,50)` = `#f7f7f8` (`nav-controls.scss`), and the button's own
//    `rgba(color(red,400),0.1)` composites over THAT — `rgb(248,229,230)`.
//    Reading the button's computed `background-color` in a browser returns the
//    declared `rgba(...)`, which is what an earlier revision of this comment
//    measured; compositing that over white inflates the destructive ratio by
//    ~0.18 and the amber ones by ~0.10. Walking the DOM parents does not find
//    the backdrop either — the trigger is SLOTTED, so the painted ancestor is
//    reachable only through `assignedSlot`.
//
//    Against the real backdrop: gold 500 is 1.60:1 on this button, and 1.78:1
//    even on the DS `validation-warning` variant (`rgba(#ffedb4,0.2)` over the
//    same parent). So the warning VARIANT does not rescue the warning GLYPH,
//    and no token in the palette would. Inheriting the button's own text colour
//    gives 2.83:1 — a 77% improvement, and byte-identical to the neighbouring
//    `IconError`, which hardcodes the same `color(red,400)`.
//
//    2.83:1 is PARITY, not conformance: WCAG 1.4.11 asks for 3:1 and the
//    `validation-destructive` variant is short of it for any glyph, this one and
//    `IconError` alike. Closing that gap means darkening the variant's
//    foreground in the design system, which would move an already-shipped icon,
//    so it is tracked separately rather than done here. What this change does
//    fix is the state being carried by the palette's least legible colour.
//
//    All three numbers are measured AT REST. The variant's `::before` overlay
//    (`rgba(color(red,400),0.2)` at `opacity: 1` on hover and focus-visible)
//    takes the glyph to 2.25:1, and `::after` at 0.3 on active to 2.00:1 — so
//    the design-system debt covers the states too, not just the resting one.
//    The focus RING is unaffected and passes: `color(red,400)` against the
//    white inner ring is 3.42:1.
//
//    The state stays distinguishable without colour — a triangle instead of a
//    cross, no count, and its own accessible name — which is what 1.4.1 asks
//    for.
export const IconWarning: FC<{ testId?: string }> = ({ testId }) => (
  <svg
    {...(testId && { 'test-id': testId })}
    width="14"
    height="12"
    viewBox="0 0 14 12"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{ display: 'block' }}
    // Decorative in both places it renders: on the trigger the button's own
    // `label` supplies the accessible name, and in the popover caption the
    // adjacent `<TextCaption>` says the same thing in words.
    aria-hidden="true"
  >
    <path
      clipRule="evenodd"
      fillRule="evenodd"
      d="M4.96697 0.960102C5.70625 -0.320034 7.55477 -0.320034 8.29341 0.960102L13.0011 9.11969C13.7397 10.3998 12.8155 12 11.3376 12H1.92281C0.444889 12 -0.478729 10.3998 0.25991 9.11969L4.96633 0.960102H4.96697ZM6.63051 4.31854C6.75783 4.31854 6.87993 4.36912 6.96996 4.45914C7.05998 4.54917 7.11056 4.67127 7.11056 4.79859V7.19885C7.11056 7.32617 7.05998 7.44827 6.96996 7.5383C6.87993 7.62832 6.75783 7.6789 6.63051 7.6789C6.50319 7.6789 6.38109 7.62832 6.29106 7.5383C6.20103 7.44827 6.15046 7.32617 6.15046 7.19885V4.79859C6.15046 4.67127 6.20103 4.54917 6.29106 4.45914C6.38109 4.36912 6.50319 4.31854 6.63051 4.31854ZM6.63051 9.5991C6.75783 9.5991 6.87993 9.54853 6.96996 9.4585C7.05998 9.36847 7.11056 9.24637 7.11056 9.11905C7.11056 8.99174 7.05998 8.86963 6.96996 8.77961C6.87993 8.68958 6.75783 8.639 6.63051 8.639C6.50319 8.639 6.38109 8.68958 6.29106 8.77961C6.20103 8.86963 6.15046 8.99174 6.15046 9.11905C6.15046 9.24637 6.20103 9.36847 6.29106 9.4585C6.38109 9.54853 6.50319 9.5991 6.63051 9.5991Z"
      fill="currentColor"
    />
  </svg>
);
